/* eslint-disable sonarjs/no-hardcoded-ip -- CMYK color values are not IP addresses */
interface IColorPalette {
  heading: string;
  description: string;
  colors: Array<IColor>;
}

export interface IColor {
  colorName: string;
  settings: { [key: string]: any };
}

interface ITwoColorsPalette {
  heading: string;
  description: string;
  colorSection: Array<IColors>;
}

export interface IColors {
  colors: Array<IColorsSettings>;
}

export interface IColorsSettings {
  baseColorName: string;
  topColorName: string;
  baseColorHex: string;
  topColorHex: string;
  wcagStandard: "AAA" | "AA";
}

export const getColorPalette = (palette: string): any => {
  switch (palette) {
    case "primary":
      return primary;
    case "secondary":
      return secondary;
    case "complementary":
      return complementary;
    case "blackAndWhite":
      return blackAndWhite;
    case "extendedIllustrations":
      return extendedIllustrations;
    case "extendedInfoGraphic":
      return extendedInfoGraphic;
    case "contrasts":
      return contrasts;
    default:
      return primary;
  }
};

const primary: IColorPalette = {
  heading: "Primärfärger",
  description:
    'Vår huvudfärg är en djup blå, som vi kallar "Mörkblå" och den färg som signalerar vårt varumärke.',
  colors: [
    {
      colorName: "Mörkblå",
      settings: {
        pantone: "662 C/U",
        cmyk: "100.89.0.4",
        rgb: "2, 6, 120",
        hex: "#020678",
        ncs: "S 5540-R70B",
        ral: "5013",
      },
    },
  ],
};

const secondary: IColorPalette = {
  heading: "Sekundärfärger",
  description:
    'Den Mörkblå färgen kompletteras med fyra sekundärfärger, "Klarblå",  "Blå 3", "Blå 4" och "Klargrön". De fyra färgerna lättar upp och ger en varmare och mer dynamisk upplevelse av det Mörkblå, för att skapa ett bredare spektra.',
  colors: [
    {
      colorName: "Klarblå",
      settings: {
        pantone: "2728 C/U",
        cmyk: "95.62.0.0",
        rgb: "0.60.210",
        hex: "#003CD2",
        ncs: "S 2565-R80B",
        ral: "5005",
      },
    },
    {
      colorName: "Blå 3",
      settings: {
        pantone: "292 C/U",
        cmyk: "55.13.0.0",
        rgb: "125.205.255",
        hex: "#7DCDFF",
        ncs: "S 1050-R90B",
        ral: "5012",
      },
    },
    {
      colorName: "Blå 4",
      settings: {
        pantone: "290 C/U",
        cmyk: "25.1.0.0",
        rgb: "200.235.250",
        hex: "#C8EBFA",
        ncs: "S 1020-B (125)",
        ral: "5024",
      },
    },
    {
      colorName: "Klargrön",
      settings: {
        pantone: "2268 C/U",
        cmyk: "64.0.77.0",
        rgb: "55.225.100",
        hex: "#37E164",
        ncs: "S 0570-G207",
        ral: "6018",
      },
    },
  ],
};

const complementary: IColorPalette = {
  heading: "Komplementfärger",
  description:
    "För att komplettera huvudfärgerna finns även en palett av av andra färger som kan kombineras med dessa för att skapa en större färgrymd och variation. Dessa används mer sparsmakat och ska tillföra dynamik, energi och värme.",
  colors: [
    {
      colorName: "Grön 3",
      settings: {
        pantone: "343 C/U",
        cmyk: "87.13.72.56",
        rgb: "0.85.65",
        hex: "#005541",
        ncs: "S 6030-G",
        ral: "6035",
      },
    },
    {
      colorName: "Grön 2",
      settings: {
        pantone: "2253 C/U",
        cmyk: "21.0.21.0",
        rgb: "185.245.225",
        hex: "#B9F5E1",
        ncs: "S 0530-G10Y",
        ral: "6019",
      },
    },
    {
      colorName: "Rosa 1",
      settings: {
        pantone: "169 C/U",
        cmyk: "0.45.35.0",
        rgb: "249.155.143",
        hex: "#F99B8F",
        ncs: "S 0540-R (77)",
        ral: "3014",
      },
    },
    {
      colorName: "Gul 1",
      settings: {
        pantone: "141 C/U",
        cmyk: "0.35.70.0",
        rgb: "250.180.85",
        hex: "#FAB455",
        ncs: "S 1060-Y10R",
        ral: "1023",
      },
    },
  ],
};

const blackAndWhite: IColorPalette = {
  heading: "Svart och vitt",
  description:
    "Svart används i print där hög läsbarhet krävs. Vit används till texter och till logotyp mot mörk bakgrund.",
  colors: [
    {
      colorName: "Black",
      settings: {
        pantone: "—",
        cmyk: "0.0.0.100",
        rgb: "13.13.13",
        hex: "#0D0D0D",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "White",
      settings: {
        pantone: "—",
        cmyk: "0.0.0.0",
        rgb: "253.253.253",
        hex: "#FDFDFD",
        ncs: "—",
        ral: "—",
      },
    },
  ],
};

const extendedIllustrations: IColorPalette = {
  heading: "Utökad färgpalett för illustrationer",
  description:
    "I första hand använder vi vår färgpalett för att färgsätta våra illustrationer. För de emotionella illustrationerna finns det större behov av en utökad färgpalett. Färgerna ska upplevas varma och jordnära och hjälpa till att illustrera mångfald och miljöer. Denna utökade färgpalett används endast som komplement i våra illustrationer.",
  colors: [
    {
      colorName: "Illustration 1",
      settings: {
        pantone: "—",
        cmyk: "57.60.57.65",
        rgb: "66, 54, 51",
        hex: "#423633",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "Illustration 2",
      settings: {
        pantone: "—",
        cmyk: "44.54.50.45",
        rgb: "107, 84, 79",
        hex: "#6B544F",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "Illustration 3",
      settings: {
        pantone: "—",
        cmyk: "59.83.68.86",
        rgb: "41, 10, 5",
        hex: "#290A05",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "Illustration 4",
      settings: {
        pantone: "—",
        cmyk: "40.81.77.65",
        rgb: "82, 36, 26",
        hex: "#52241A",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "Illustration 5",
      settings: {
        pantone: "—",
        cmyk: "0.20.41.0",
        rgb: "255, 214, 163",
        hex: "#FFD6A3",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "Illustration 6",
      settings: {
        pantone: "—",
        cmyk: "0.11.48.0",
        rgb: "255, 227, 153",
        hex: "#FFE399",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "Illustration 7",
      settings: {
        pantone: "—",
        cmyk: "15.89.57.5",
        rgb: "201, 54, 79",
        hex: "#C9364F",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "Illustration 8",
      settings: {
        pantone: "—",
        cmyk: "0.24.16.0",
        rgb: "250, 209, 204",
        hex: "#FAD1CC",
        ncs: "—",
        ral: "—",
      },
    },
  ],
};

const extendedInfoGraphic: IColorPalette = {
  heading: "Utökade färger för infografik",
  description:
    "Färgsättning i infografik görs primärt i blått och grönt från färgpaletten. Använd till exempel den ljusblå bakgrundsfärgen som neutral komplementfärg. Den gröna färgen kan användas för att lyfta fram information. Som komplement till den blågröna färgskalan finns ytterligare två färgställningar i orange och rött. De används då den blågröna färgskalan inte räcker till för att åtskilja information eller värden färgmässigt på ett tydligt sätt. Denna utökade färgpalett används endast som komplement i vår infografik.",
  colors: [
    {
      colorName: "Infografik 1",
      settings: {
        pantone: "—",
        cmyk: "10.71.100.0",
        rgb: "218, 97, 12",
        hex: "#DA610C",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "Infografik 2",
      settings: {
        pantone: "—",
        cmyk: "0.11.48.0",
        rgb: "255, 227, 153",
        hex: "#FFE399",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "Infografik 3",
      settings: {
        pantone: "—",
        cmyk: "15.89.57.5",
        rgb: "201, 54, 79",
        hex: "#C9364F",
        ncs: "—",
        ral: "—",
      },
    },
    {
      colorName: "Infografik 4",
      settings: {
        pantone: "—",
        cmyk: "0.24.16.0",
        rgb: "250, 209, 204",
        hex: "#FAD1CC",
        ncs: "—",
        ral: "—",
      },
    },
  ],
};

const contrasts: ITwoColorsPalette = {
  heading: "Kontrastfärger",
  description:
    "Det finns 28 tillåtna färgkombinationer inom vårt färgsystem (exklusive svart och vitt) i vilka god kontrast erhålls. Primärt skall den Mörkblå färgen vara dominerande. Sekundärt kan någon av sekundär-färgerna vara dominerande. Exempel där komplementfärger är dominerande skall betraktas som undantag och får bara finnas i kombination med ovan nämnda exempel.Alla dessa färgkombinationer är testade enligt WCAGs (Web Content Accessibility Guidelines) riktlinjer för läsbarhet och användarvänlighet i digitala kanaler. Här visas de färgkombinationer som får godkänt enligt WCAGs standard för AAA.",
  colorSection: [
    {
      colors: [
        {
          baseColorName: "Mörkblå",
          topColorName: "Vit",
          baseColorHex: "#020678",
          topColorHex: "#FDFDFD",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Gul 1",
          topColorName: "Mörkblå",
          baseColorHex: "#020678",
          topColorHex: "#FAB455",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Mörkblå",
          topColorName: "Rosa 1",
          baseColorHex: "#020678",
          topColorHex: "#F99B8F",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Mörkblå",
          topColorName: "Blå 3",
          baseColorHex: "#020678",
          topColorHex: "#7DCDFF",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Mörkblå",
          topColorName: "Blå 4",
          baseColorHex: "#020678",
          topColorHex: "#C8EBFA",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Mörkblå",
          topColorName: "Klargrön",
          baseColorHex: "#020678",
          topColorHex: "#37E164",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Mörkblå",
          topColorName: "Grön 2",
          baseColorHex: "#020678",
          topColorHex: "#B9F5E1",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Blå 3",
          topColorName: "Mörkblå",
          baseColorHex: "#7DCDFF",
          topColorHex: "#020678",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Blå 3",
          topColorName: "Klarblå",
          baseColorHex: "#7DCDFF",
          topColorHex: "#003CD2",
          wcagStandard: "AAA",
        },
      ],
    },
    {
      colors: [
        {
          baseColorName: "Klarblå",
          topColorName: "Vit",
          baseColorHex: "#003CD2",
          topColorHex: "#FDFDFD",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Klarblå",
          topColorName: "Gul 1",
          baseColorHex: "#003CD2",
          topColorHex: "#FAB455",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Klarblå",
          topColorName: "Rosa 1",
          baseColorHex: "#003CD2",
          topColorHex: "#F99B8F",
          wcagStandard: "AA",
        },
        {
          baseColorName: "Klarblå",
          topColorName: "Blå 3",
          baseColorHex: "#003CD2",
          topColorHex: "#7DCDFF",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Klarblå",
          topColorName: "Blå 4",
          baseColorHex: "#003CD2",
          topColorHex: "#C8EBFA",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Klarblå",
          topColorName: "Klargrön",
          baseColorHex: "#003CD2",
          topColorHex: "#37E164",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Klarblå",
          topColorName: "Grön 2",
          baseColorHex: "#003CD2",
          topColorHex: "#C8EBFA",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Blå 4",
          topColorName: "Mörkblå",
          baseColorHex: "#C8EBFA",
          topColorHex: "#020678",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Blå 4",
          topColorName: "Klarblå",
          baseColorHex: "#C8EBFA",
          topColorHex: "#003CD2",
          wcagStandard: "AAA",
        },
      ],
    },
    {
      colors: [
        {
          baseColorName: "Grön 2",
          topColorName: "Klarblå",
          baseColorHex: "#B9F5E1",
          topColorHex: "#003CD2",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Klargrön",
          topColorName: "Mörkblå",
          baseColorHex: "#37E164",
          topColorHex: "#020678",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Klargrön",
          topColorName: "Klarblå",
          baseColorHex: "#37E164",
          topColorHex: "#003CD2",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Grön 3",
          topColorName: "Blå 3",
          baseColorHex: "#005541",
          topColorHex: "#7DCDFF",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Grön 3",
          topColorName: "Blå 4",
          baseColorHex: "#005541",
          topColorHex: "#C8EBFA",
          wcagStandard: "AAA",
        },
      ],
    },
    {
      colors: [
        {
          baseColorName: "Gul 1",
          topColorName: "Mörkblå",
          baseColorHex: "#FAB455",
          topColorHex: "#020678",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Gul 1",
          topColorName: "Klarblå",
          baseColorHex: "#FAB455",
          topColorHex: "#003CD2",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Rosa 1",
          topColorName: "Mörkblå",
          baseColorHex: "#F99B8F",
          topColorHex: "#020678",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Vit",
          topColorName: "Mörkblå",
          baseColorHex: "#FDFDFD",
          topColorHex: "#020678",
          wcagStandard: "AAA",
        },
        {
          baseColorName: "Vit",
          topColorName: "Klarblå",
          baseColorHex: "#FDFDFD",
          topColorHex: "#003CD2",
          wcagStandard: "AAA",
        },
      ],
    },
  ],
};
